import React, { useMemo, useContext, useRef, useReducer, useCallback } from 'react';
import isEqual from 'fast-deep-equal/react';
import { getResponsiveValue, depthFirstObjectMerge, parseScaleValuePath, isScaleValue, getScaleValue, LRUCache, omitProps } from '@byteclaw/visage-utils';
import parseColor from 'color';

function createCache() {
  var cache = new WeakMap();
  var overrideCache = new WeakMap();
  return {
    get: function get(faceStyleSheet, parentStyleSheets, localStyleSheet) {
      var _cache$get, _cache$get$get;

      return (_cache$get = cache.get(faceStyleSheet)) == null ? void 0 : (_cache$get$get = _cache$get.get(parentStyleSheets)) == null ? void 0 : _cache$get$get.get(localStyleSheet);
    },
    getByOverride: function getByOverride(faceStyleSheet, parentStyleSheets, styleOverrideStyleSheet, localStyleSheet) {
      var _overrideCache$get, _overrideCache$get$ge, _overrideCache$get$ge2;

      return (_overrideCache$get = overrideCache.get(faceStyleSheet)) == null ? void 0 : (_overrideCache$get$ge = _overrideCache$get.get(parentStyleSheets)) == null ? void 0 : (_overrideCache$get$ge2 = _overrideCache$get$ge.get(styleOverrideStyleSheet)) == null ? void 0 : _overrideCache$get$ge2.get(localStyleSheet);
    },
    set: function set(faceStyleSheet, parentStyleSheets, localStyleSheet) {
      var byFace = cache.get(faceStyleSheet);

      if (!byFace) {
        var styles = [localStyleSheet, faceStyleSheet].concat(parentStyleSheets);
        cache.set(faceStyleSheet, new WeakMap([[parentStyleSheets, new WeakMap([[localStyleSheet, styles]])]]));
        return styles;
      }

      var byParentStyles = byFace.get(parentStyleSheets);

      if (!byParentStyles) {
        var _styles = [localStyleSheet, faceStyleSheet].concat(parentStyleSheets);

        byFace.set(parentStyleSheets, new WeakMap([[localStyleSheet, _styles]]));
        return _styles;
      }

      var byLocalStyles = byParentStyles.get(localStyleSheet);

      if (!byLocalStyles) {
        var _styles2 = [localStyleSheet, faceStyleSheet].concat(parentStyleSheets);

        byParentStyles.set(localStyleSheet, _styles2);
        return _styles2;
      }

      return byLocalStyles;
    },
    setByOverride: function setByOverride(faceStyleSheet, parentStyleSheets, styleOverrideStyleSheet, localStyleSheet) {
      var byFace = overrideCache.get(faceStyleSheet);

      if (!byFace) {
        var styles = [localStyleSheet, faceStyleSheet].concat(parentStyleSheets, [styleOverrideStyleSheet]);
        overrideCache.set(faceStyleSheet, new WeakMap([[parentStyleSheets, new WeakMap([[styleOverrideStyleSheet, new WeakMap([[localStyleSheet, styles]])]])]]));
        return styles;
      }

      var byParentStyles = byFace.get(parentStyleSheets);

      if (!byParentStyles) {
        var _styles3 = [localStyleSheet, faceStyleSheet].concat(parentStyleSheets, [styleOverrideStyleSheet]);

        byFace.set(parentStyleSheets, new WeakMap([[styleOverrideStyleSheet, new WeakMap([[localStyleSheet, _styles3]])]]));
        return _styles3;
      }

      var byStyleOverride = byParentStyles.get(styleOverrideStyleSheet);

      if (!byStyleOverride) {
        var _styles4 = [localStyleSheet, faceStyleSheet].concat(parentStyleSheets, [styleOverrideStyleSheet]);

        byParentStyles.set(styleOverrideStyleSheet, new WeakMap([[localStyleSheet, _styles4]]));
        return _styles4;
      }

      var byLocalStyles = byStyleOverride.get(localStyleSheet);

      if (!byLocalStyles) {
        var _styles5 = [localStyleSheet, faceStyleSheet].concat(parentStyleSheets, [styleOverrideStyleSheet]);

        byStyleOverride.set(localStyleSheet, _styles5);
        return _styles5;
      }

      return byLocalStyles;
    }
  };
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;

  _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _construct(Parent, args, Class) {
  if (_isNativeReflectConstruct()) {
    _construct = Reflect.construct;
  } else {
    _construct = function _construct(Parent, args, Class) {
      var a = [null];
      a.push.apply(a, args);
      var Constructor = Function.bind.apply(Parent, a);
      var instance = new Constructor();
      if (Class) _setPrototypeOf(instance, Class.prototype);
      return instance;
    };
  }

  return _construct.apply(null, arguments);
}

function _isNativeFunction(fn) {
  return Function.toString.call(fn).indexOf("[native code]") !== -1;
}

function _wrapNativeSuper(Class) {
  var _cache = typeof Map === "function" ? new Map() : undefined;

  _wrapNativeSuper = function _wrapNativeSuper(Class) {
    if (Class === null || !_isNativeFunction(Class)) return Class;

    if (typeof Class !== "function") {
      throw new TypeError("Super expression must either be null or a function");
    }

    if (typeof _cache !== "undefined") {
      if (_cache.has(Class)) return _cache.get(Class);

      _cache.set(Class, Wrapper);
    }

    function Wrapper() {
      return _construct(Class, arguments, _getPrototypeOf(this).constructor);
    }

    Wrapper.prototype = Object.create(Class.prototype, {
      constructor: {
        value: Wrapper,
        enumerable: false,
        writable: true,
        configurable: true
      }
    });
    return _setPrototypeOf(Wrapper, Class);
  };

  return _wrapNativeSuper(Class);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

var VisageComponentSymbol = '@@vsg';

/**
 * Returns a display name of a component
 */

function displayName(Component) {
  return Component.displayName || Component.name || (typeof Component === 'string' && Component.length > 0 ? Component : 'Unknown');
}
/**
 * Marks a component as Visage component
 */

function markAsVisageComponent(component) {
  // eslint-disable-next-line no-param-reassign
  component[VisageComponentSymbol] = true;
  return component;
}
/**
 * Detects if component is a Visage component
 */

function isVisageComponent(component) {
  if (typeof component === 'string') {
    return false;
  }

  return !!component[VisageComponentSymbol];
}

var VisageContext = /*#__PURE__*/React.createContext(undefined);
VisageContext.displayName = 'VisageContext';

var StylerResultType;

(function (StylerResultType) {
  /**
   * Pre styler that returns a StyleSheet that needs to be resolved
   * the result is then prepended to current StyleSheet which works as extend
   */
  StylerResultType["pre"] = "pre";
  /**
   * Pre styler that returns a StyleSheet that won't be resolved
   * the result is then prepended to current StyleSheet which works as extend
   */

  StylerResultType["preFinal"] = "preFinal";
  /**
   * In place styler returns a StyleSheet that needs to be resolved
   * the result is spread to current StyleSheet meaning that it works as local extend
   * but can be overwrote by subsequent props
   */

  StylerResultType["inPlace"] = "inPlace";
  /**
   * In place styler returns a StyleSheet that won't be resolved
   * the result is spread to current StyleSheet meaning that it works as local extend
   * but can be overwrote by subsequent props
   */

  StylerResultType["inPlaceFinal"] = "inPlaceFinal";
  /**
   * Post styler that returns a StyleSHeet that needs to be resolved
   * the result is then apended to current StyleSheet which works as override
   */

  StylerResultType["post"] = "post";
  /**
   * Post styler that returns a StyleSHeet that won't be resolved
   * the result is then apended to current StyleSheet which works as override
   */

  StylerResultType["postFinal"] = "postFinal";
})(StylerResultType || (StylerResultType = {}));

function createStyler(type, styler) {
  return function () {
    return {
      type: type,
      styles: styler.apply(void 0, arguments)
    };
  };
}
function inPlaceStyler(styler) {
  return createStyler(StylerResultType.inPlace, styler);
}
function inPlaceFinalStyler(styler) {
  return createStyler(StylerResultType.inPlaceFinal, styler);
}
function preStyler(styler) {
  return createStyler(StylerResultType.pre, styler);
}
function preFinalStyler(styler) {
  return createStyler(StylerResultType.preFinal, styler);
}
function postStyler(styler) {
  return createStyler(StylerResultType.post, styler);
}
function postFinalStyler(styler) {
  return createStyler(StylerResultType.postFinal, styler);
}

var catchAllStyler = /*#__PURE__*/inPlaceFinalStyler(function (propName, value, ctx) {
  var _ctx$resolvers$propNa, _ref;

  var resolver = (_ctx$resolvers$propNa = ctx.resolvers[propName]) != null ? _ctx$resolvers$propNa : ctx.resolvers.themeKey;
  var resolvedValue = resolver(propName, value, ctx);

  if (typeof resolvedValue === 'object' && resolvedValue !== null) {
    return resolvedValue;
  }

  return _ref = {}, _ref[propName] = ctx.formatters[propName] ? ctx.formatters[propName](propName, resolvedValue, ctx) : resolvedValue, _ref;
});
var faceStyler = /*#__PURE__*/postStyler(function (_, value, ctx) {
  if (ctx.theme.faces == null) {
    return {};
  }

  return typeof value !== 'string' ? {} : ctx.theme.faces[value] || {};
});
var extendsStyler = /*#__PURE__*/preStyler(function (_, value, ctx) {
  if (ctx.theme.mixins == null) {
    return {};
  }

  return typeof value !== 'string' ? {} : ctx.theme.mixins[value] || {};
});

/**
 * Resolves style sheet
 */

function resolveStyleSheet(styleSheet, ctx) {
  var cache = ctx.resolutionCache.get(ctx.breakpoint);

  if (!cache) {
    ctx.resolutionCache.set(ctx.breakpoint, new WeakMap());
  }

  if (cache != null && cache.has(styleSheet)) {
    return cache.get(styleSheet);
  }

  var keys = Object.keys(styleSheet);
  var keysLength = keys.length;
  var preSheets = [];
  var postSheets = [];
  var resolvedStyleSheet = {};

  for (var i = 0; i < keysLength; i++) {
    var key = keys[i];
    var value = styleSheet[key];

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      resolvedStyleSheet[key] = resolveStyleSheet(value, ctx);
    } else {
      var _ctx$stylers$key;

      var responsiveValue = getResponsiveValue(ctx.breakpoint, value);
      var styler = (_ctx$stylers$key = ctx.stylers[key]) != null ? _ctx$stylers$key : ctx.stylers.catchAll;
      var result = styler(key, responsiveValue, ctx);

      switch (result.type) {
        case StylerResultType.pre:
          {
            preSheets.push(resolveStyleSheet(result.styles, ctx));
            break;
          }

        case StylerResultType.post:
          {
            postSheets.push(resolveStyleSheet(result.styles, ctx));
            break;
          }

        case StylerResultType.inPlace:
          {
            resolvedStyleSheet = depthFirstObjectMerge(resolvedStyleSheet, resolveStyleSheet(result.styles, ctx));
            break;
          }

        case StylerResultType.inPlaceFinal:
          {
            resolvedStyleSheet = depthFirstObjectMerge(resolvedStyleSheet, result.styles);
            break;
          }

        case StylerResultType.preFinal:
          {
            preSheets.push(result.styles);
            break;
          }

        case StylerResultType.postFinal:
          {
            postSheets.push(result.styles);
            break;
          }
      }
    }
  }

  var finalStyleSheet = depthFirstObjectMerge.apply(void 0, preSheets.concat([resolvedStyleSheet], postSheets));
  cache == null ? void 0 : cache.set(styleSheet, finalStyleSheet);
  return finalStyleSheet;
}

/**
 * Resolves style sheets and merges them using depth first algorithm from right to left
 */

function resolveStyleSheets(styleSheets, ctx) {
  return depthFirstObjectMerge.apply(void 0, styleSheets.map(function (styleSheet) {
    return resolveStyleSheet(styleSheet, ctx);
  }));
}

var themeKeyResolver = function themeKeyResolver(propName, value, ctx) {
  if (value == null) {
    return value;
  }

  var theme = ctx.theme;
  var themeValue = theme[propName];

  switch (typeof themeValue) {
    case 'undefined':
      return value;

    case 'object':
      {
        // theme value is object, it can be null, ScaleValue or nested object
        if (themeValue == null) {
          return themeValue;
        } // return array theme values as is


        if (Array.isArray(themeValue)) {
          return themeValue;
        }

        var _parseScaleValuePath = parseScaleValuePath(value),
            propertyName = _parseScaleValuePath[0],
            scaleIndex = _parseScaleValuePath[1];

        if (isScaleValue(themeValue)) {
          // there is a catch, basically we can support Scales that are not nested and the offset
          // can be set up directly as fontSize={-1} for example
          return getScaleValue(themeValue, scaleIndex) || value;
        }

        var nestedValue = themeKeyResolver(propertyName, value, _extends({}, ctx, {
          // pass the theme value down because we are resolving nested value
          // for examle borderRadius.controlBorderRadus
          theme: themeValue
        })); // null turns off the value

        if (nestedValue === null) {
          return null;
        }

        if (nestedValue != null) {
          return nestedValue;
        }

        return value;
      }

    default:
      return themeValue;
  }
};

/**
 * Uses statically declared function in useMemo
 *
 * Memo is invalidated only on deps change, fn is ignored
 */

function useStaticMemo(fn, deps) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(function () {
    return fn.apply(void 0, deps);
  }, deps);
}

function createDesignSystem(breakpoint, theme, styleGenerator, _ref) {
  if (breakpoint === void 0) {
    breakpoint = 0;
  }

  var generation = _ref.generation,
      resolution = _ref.resolution,
      styler = _ref.styler,
      styleSheet = _ref.styleSheet;

  if (!resolution.has(breakpoint)) {
    resolution.set(breakpoint, new WeakMap());
  }

  if (!generation.has(breakpoint)) {
    generation.set(breakpoint, new WeakMap());
  }

  if (!styleSheet.has(breakpoint)) {
    styleSheet.set(breakpoint, createCache());
  }

  var generationCache = generation.get(breakpoint);
  var resolutionForBreakpointCache = resolution.get(breakpoint);
  var styleSheetCache = styleSheet.get(breakpoint);
  var resolveCtx = {
    breakpoint: breakpoint,
    format: theme.format,
    resolve: theme.resolve,
    style: theme.style,
    formatters: theme.formatters,
    resolvers: theme.resolvers,
    resolutionCache: resolution,
    stylers: theme.stylers,
    stylerCache: styler,
    theme: theme.theme
  };
  return {
    breakpoint: breakpoint,
    styleSheetCache: styleSheetCache,
    ctx: resolveCtx,
    generate: function generate(styleSheets) {
      var result = generationCache.get(styleSheets);

      if (!result) {
        result = styleGenerator(styleSheets, resolveCtx);
        generationCache.set(styleSheets, result);
      }

      return result;
    },
    resolveStyleSheets: function resolveStyleSheets$1(styleSheets) {
      var result = resolutionForBreakpointCache.get(styleSheets);

      if (!result) {
        result = resolveStyleSheets(styleSheets, resolveCtx);
        resolutionForBreakpointCache.set(styleSheets, result);
      }

      return result;
    },
    theme: resolveCtx.theme
  };
}
/**
 * Use design system works as root hook that can be used to create own DesignSystem
 *
 * If no options are provided then it works as useContext that connects to Visage context
 */


function useDesignSystem(options) {
  // if options are provided, we want to create new Visage instance
  // otherwise we want to connect to parent
  var ctx = useContext(VisageContext);

  if (!ctx) {
    if (!options) {
      throw new Error("Options must be provided to useDesignSystem if used for first time");
    }
  } // if we pass options, we want to create new design system root
  // we know that useRef can be called in condition
  // because otherwise the component would throw

  /* eslint-disable react-hooks/rules-of-hooks */


  if (options) {
    var cacheRef = useRef();
    var generatorRef = useRef(options.styleGenerator);
    var themeRef = useRef(options.theme);

    if (!cacheRef.current || generatorRef.current !== options.styleGenerator || themeRef.current !== options.theme) {
      cacheRef.current = {
        generation: new Map(),
        resolution: new Map(),
        styleSheet: new Map(),
        styler: new LRUCache(1000)
      };
      generatorRef.current = options.styleGenerator;
      themeRef.current = options.theme;
    }

    return useStaticMemo(createDesignSystem, [options.is, options.theme, options.styleGenerator, cacheRef.current]);
  }
  /* eslint-enable react-hooks/rules-of-hooks */
  // ctx is defined because there is a check above


  return ctx;
}

function deduplicateVariantSettings(variantSettings, parentVariantSettings) {
  // removes duplicit variants
  // basically the variants from parent always win, because a developer
  // said that it needs to behave like this, for example if on Button is a variant that is stripped
  // but I specify on extending component that I don't want to strip it
  // then this information needs to win
  // keeps the information about registered prop to name combinations
  var registry = {};
  return [].concat(variantSettings, parentVariantSettings).reduceRight(function (res, settings) {
    if (registry[settings.prop + "-" + settings.name]) {
      return res;
    }

    registry[settings.prop + "-" + settings.name] = true;
    return [settings].concat(res);
  }, []);
}

function useVariantSettings(variantSettings, parentVariantSettings) {
  return useStaticMemo(deduplicateVariantSettings, [variantSettings, parentVariantSettings]);
}

var _excluded = ["parentStyles", "styles", "$$variants"];
var DEFAULT_PARENT_STYLES = [];
var DEFAULT_VARIANT_PROCESSORS = [];
/**
 * This hook contains the logic for visage component
 *
 * Using this hook you can implement your own custom component that uses visage
 */

function useVisage(as, _ref, options) {
  var _ref$parentStyles = _ref.parentStyles,
      parentStyles = _ref$parentStyles === void 0 ? DEFAULT_PARENT_STYLES : _ref$parentStyles,
      styles = _ref.styles,
      _ref$$$variants = _ref.$$variants,
      $$variants = _ref$$$variants === void 0 ? DEFAULT_VARIANT_PROCESSORS : _ref$$$variants,
      restProps = _objectWithoutPropertiesLoose(_ref, _excluded);

  var visage = useDesignSystem();
  var propsRef = useRef();
  var variantSettings = useVariantSettings(options.variants, $$variants);
  var styleSheetRef = useRef(); // now resolve style sheet and store it under styles

  if (typeof options.defaultStyles === 'function') {
    if (!isEqual(propsRef.current, restProps)) {
      styleSheetRef.current = options.defaultStyles(restProps);
      propsRef.current = restProps;
    }
  } else if (styleSheetRef.current !== options.defaultStyles) {
    styleSheetRef.current = options.defaultStyles;
  }

  var localStyles = visage.styleSheetCache.get(options.faceStyleSheet, parentStyles, styleSheetRef.current);

  if (!localStyles) {
    localStyles = visage.styleSheetCache.set(options.faceStyleSheet, parentStyles, styleSheetRef.current);
  } // strip styles, parentStyles from props
  // if component is visage component, pass parentStyles and styles
  // otherwise generate styles


  if (!isVisageComponent(as)) {
    var finalStyleSheets = localStyles;

    if (styles) {
      var cachedStyles = visage.styleSheetCache.getByOverride(options.faceStyleSheet, parentStyles, styles, styleSheetRef.current);

      if (!cachedStyles) {
        finalStyleSheets = visage.styleSheetCache.setByOverride(options.faceStyleSheet, parentStyles, styles, styleSheetRef.current);
      } else {
        finalStyleSheets = cachedStyles;
      }
    } // some style generators do not output class names so be careful, this is just general purpose


    var styleProps = visage.generate(finalStyleSheets); // process props using variant processors
    // this will strip props according to processor settings

    var passProps = omitProps(restProps, variantSettings);
    var className = styleProps.className || passProps.className ? ((styleProps.className || '') + " " + (passProps.className || '')).trim() : undefined;
    return _extends({}, passProps, styleProps, {
      className: className
    });
  }

  return _extends({}, restProps, {
    parentStyles: localStyles,
    styles: styles,
    $$variants: variantSettings
  });
}

var _excluded$1 = ["as"];
var DEFAULT_STYLE_SHEET = {};
/**
 * Creates a Visage component
 */

function createComponent(defaultAs, _temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$asMemo = _ref.asMemo,
      asMemo = _ref$asMemo === void 0 ? true : _ref$asMemo,
      defaultProps = _ref.defaultProps,
      defaultStyles = _ref.defaultStyles,
      styles = _ref.styles,
      name = _ref.displayName,
      variants = _ref.variants;

  var componentName = displayName(name || defaultAs);
  var faceStyleSheet = {
    face: componentName
  };
  var componentOptions = {
    componentName: componentName,
    defaultStyles: styles || defaultStyles || DEFAULT_STYLE_SHEET,
    faceStyleSheet: faceStyleSheet,
    variants: variants || []
  };
  var defProps = defaultProps || {};

  var RawComponent = function RawComponent(_ref2, ref) {
    var _ref2$as = _ref2.as,
        as = _ref2$as === void 0 ? defaultAs : _ref2$as,
        restProps = _objectWithoutPropertiesLoose(_ref2, _excluded$1);

    var props = useVisage(as, _extends({}, defProps, restProps, {
      children: restProps.children || defProps.children
    }), componentOptions);
    return React.createElement(as, _extends({}, props, {
      ref: ref
    }));
  };

  RawComponent.displayName = "VisageComponent(" + componentName + ")";
  var ComponentWithForwardRef = React.forwardRef(RawComponent);
  var Component = markAsVisageComponent(asMemo ? React.memo(ComponentWithForwardRef) : ComponentWithForwardRef);
  Component.displayName = RawComponent.displayName;
  return Component;
}

function DesignSystem(_ref) {
  var children = _ref.children,
      _ref$is = _ref.is,
      is = _ref$is === void 0 ? 0 : _ref$is,
      styleGenerator = _ref.styleGenerator,
      theme = _ref.theme;
  var visage = useDesignSystem({
    is: is,
    styleGenerator: styleGenerator,
    theme: theme
  });
  return React.createElement(VisageContext.Provider, {
    value: visage
  }, children);
}

function init(_ref) {
  var defaultBreakpoint = _ref.defaultBreakpoint,
      breakpoints = _ref.breakpoints;
  var matches = breakpoints.map(function () {
    return false;
  });
  matches[defaultBreakpoint] = true;
  return {
    matches: matches,
    viewport: matches.lastIndexOf(true)
  };
}

function reducer(previousState, action) {
  if (action.type === 'SET_BREAKPOINT') {
    var matches = previousState.matches.slice();
    matches[action.index] = action.matches;
    var greatestBreakpoint = matches.lastIndexOf(true);
    return {
      matches: matches,
      // if there is no breakpoint matched at the moment, use the old one
      // this is an edge case if you use exclusive media queries where only one is matched at the time
      // in that case it can happen that first is a breakpoint unset and then is set for new one
      // this breaks the application so we use previous viewport to be sure
      viewport: greatestBreakpoint === -1 ? previousState.viewport : greatestBreakpoint
    };
  }

  return previousState;
}

function useBreakpointManager(defaultBreakpoint, breakpoints) {
  var defaultBreakpointRef = useRef(defaultBreakpoint);

  var _useReducer = useReducer(reducer, {
    defaultBreakpoint: defaultBreakpoint,
    breakpoints: breakpoints
  }, init),
      state = _useReducer[0],
      dispatch = _useReducer[1];

  var setBreakpoint = useCallback(function (index, matches) {
    if (matches === void 0) {
      matches = true;
    }

    dispatch({
      type: 'SET_BREAKPOINT',
      index: index,
      matches: matches
    });
  }, [dispatch]); // if default breakpoint changed, set breakpoint

  if (defaultBreakpointRef.current !== defaultBreakpoint) {
    var previousBreadkpoint = defaultBreakpointRef.current;
    defaultBreakpointRef.current = defaultBreakpoint;
    setBreakpoint(previousBreadkpoint, false);
    setBreakpoint(defaultBreakpoint, true);
  }

  return [state.viewport, setBreakpoint];
}
function useBreakpoint(_ref2) {
  var gte = _ref2.gte,
      is = _ref2.is,
      lte = _ref2.lte,
      not = _ref2.not;
  var visage = useDesignSystem();
  return useMemo(function () {
    // now check the breakpoints, basically start with more precise breakpoints
    var isMatch = false;

    if (is != null) {
      isMatch = is === visage.breakpoint || Array.isArray(is) && is.indexOf(visage.breakpoint) !== -1;
    } else if (not != null) {
      isMatch = Array.isArray(not) ? not.indexOf(visage.breakpoint) === -1 : not !== visage.breakpoint;
    } else if (gte != null) {
      isMatch = visage.breakpoint >= gte;
    } else if (lte != null) {
      isMatch = visage.breakpoint <= lte;
    }

    return isMatch;
  }, [visage, gte, is, lte, not]);
}

var _excluded$2 = ["children"];
function IsBreakpoint(_ref) {
  var children = _ref.children,
      restProps = _objectWithoutPropertiesLoose(_ref, _excluded$2);

  var shouldRender = useBreakpoint(restProps);

  if (shouldRender) {
    return typeof children === 'function' ? children() : children;
  }

  return null;
}

/**
 * Pixel formatter, exports number as px (number is rounded)
 */
var px = function px(propName, value) {
  if (typeof value !== 'number') {
    return value;
  }

  return Math.round(value) + "px";
};
/**
 * Size unit formatter, exports number as px (if integer) or as % if floating
 */


var sizeUnit = function sizeUnit(propName, value) {
  if (typeof value !== 'number') {
    return value;
  }

  if (Number.isInteger(value)) {
    return value + "px";
  }

  return value + "%";
};

var formatters = {
  px: px,
  sizeUnit: sizeUnit
};

// tslint:disable:only-arrow-functions
// tslint:disable:object-literal-shorthand
// tslint:disable:trailing-comma
// tslint:disable:object-literal-sort-keys
// tslint:disable:one-variable-per-declaration
// tslint:disable:max-line-length
// tslint:disable:no-consecutive-blank-lines
// tslint:disable:align
var SyntaxError = /*#__PURE__*/function (_Error) {
  _inheritsLoose(SyntaxError, _Error);

  function SyntaxError(message, expected, found, location) {
    var _this;

    _this = _Error.call(this) || this;
    _this.message = message;
    _this.expected = expected;
    _this.found = found;
    _this.location = location;
    _this.name = 'SyntaxError';

    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(_assertThisInitialized(_this), SyntaxError);
    }

    return _this;
  }

  SyntaxError.buildMessage = function buildMessage(expected, found) {
    function hex(ch) {
      return ch.charCodeAt(0).toString(16).toUpperCase();
    }

    function literalEscape(s) {
      return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\0/g, '\\0').replace(/\t/g, '\\t').replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/[\x00-\x0F]/g, function (ch) {
        return "\\x0" + hex(ch);
      }).replace(/[\x10-\x1F\x7F-\x9F]/g, function (ch) {
        return "\\x" + hex(ch);
      });
    }

    function classEscape(s) {
      return s.replace(/\\/g, '\\\\').replace(/\]/g, '\\]').replace(/\^/g, '\\^').replace(/-/g, '\\-').replace(/\0/g, '\\0').replace(/\t/g, '\\t').replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/[\x00-\x0F]/g, function (ch) {
        return "\\x0" + hex(ch);
      }).replace(/[\x10-\x1F\x7F-\x9F]/g, function (ch) {
        return "\\x" + hex(ch);
      });
    }

    function describeExpectation(expectation) {
      switch (expectation.type) {
        case 'literal':
          return "\"" + literalEscape(expectation.text) + "\"";

        case 'class':
          var escapedParts = expectation.parts.map(function (part) {
            return Array.isArray(part) ? classEscape(part[0]) + "-" + classEscape(part[1]) : classEscape(part);
          });
          return "[" + (expectation.inverted ? '^' : '') + escapedParts + "]";

        case 'any':
          return 'any character';

        case 'end':
          return 'end of input';

        case 'other':
          return expectation.description;
      }
    }

    function describeExpected(expected1) {
      var descriptions = expected1.map(describeExpectation);
      var i;
      var j;
      descriptions.sort();

      if (descriptions.length > 0) {
        for (i = 1, j = 1; i < descriptions.length; i++) {
          if (descriptions[i - 1] !== descriptions[i]) {
            descriptions[j] = descriptions[i];
            j++;
          }
        }

        descriptions.length = j;
      }

      switch (descriptions.length) {
        case 1:
          return descriptions[0];

        case 2:
          return descriptions[0] + " or " + descriptions[1];

        default:
          return descriptions.slice(0, -1).join(', ') + ", or " + descriptions[descriptions.length - 1];
      }
    }

    function describeFound(found1) {
      return found1 ? "\"" + literalEscape(found1) + "\"" : 'end of input';
    }

    return "Expected " + describeExpected(expected) + " but " + describeFound(found) + " found.";
  };

  return SyntaxError;
}( /*#__PURE__*/_wrapNativeSuper(Error));

function peg$parse(input, options) {
  options = options !== undefined ? options : {};
  var peg$FAILED = {};
  var peg$startRuleIndices = {
    Start: 0
  };
  var peg$startRuleIndex = 0;
  var peg$consts = [function (col) {
    return function (ctx, colorLib) {
      return col(ctx, colorLib).rgb().toString();
    };
  }, 'color(', peg$literalExpectation('color(', false), ')', peg$literalExpectation(')', false), function (color, ops) {
    return createProcessFn(color, ops);
  }, '#', peg$literalExpectation('#', false), function (c) {
    return function (ctx, colorLib) {
      return colorLib(c);
    };
  }, function (c) {
    return function (ctx, colorLib) {
      return colorLib(ctx.resolve('colors', c, ctx));
    };
  }, 'rgba', peg$literalExpectation('rgba', false), 'rgb', peg$literalExpectation('rgb', false), '(', peg$literalExpectation('(', false), function (r, g, b, a) {
    return function (ctx, colorLib) {
      return colorLib.rgb(rgbVal(r), rgbVal(g), rgbVal(b), a == null ? 1 : a.value);
    };
  }, 'hsla', peg$literalExpectation('hsla', false), 'hsl', peg$literalExpectation('hsl', false), function (h, s, l, a) {
    return function (ctx, colorLib) {
      return colorLib.hsl(h.value, s.value, l.value, a == null ? 1 : a.value);
    };
  }, function (a) {
    return a;
  }, 'if(', peg$literalExpectation('if(', false), 'isDark', peg$literalExpectation('isDark', false), 'isLight', peg$literalExpectation('isLight', false), function (predicate, ifLeaf, elseLeaf) {
    return {
      op: 'if',
      predicate: predicate,
      ifLeaf: ifLeaf,
      elseLeaf: elseLeaf
    };
  }, 'alpha', peg$literalExpectation('alpha', false), 'a', peg$literalExpectation('a', false), function (op, modifier, value) {
    return {
      op: op,
      modifier: modifier,
      value: value
    };
  }, 'hue', peg$literalExpectation('hue', false), 'h', peg$literalExpectation('h', false), 'shade', peg$literalExpectation('shade', false), 'tint', peg$literalExpectation('tint', false), function (op, value) {
    return {
      op: 'blend',
      color: function color(ctx, colorLib) {
        return op === 'shade' ? colorLib.rgb(0, 0, 0) : colorLib.rgb(255, 255, 255);
      },
      value: value
    };
  }, 'blackness', peg$literalExpectation('blackness', false), 'lightness', peg$literalExpectation('lightness', false), 'saturation', peg$literalExpectation('saturation', false), 'whiteness', peg$literalExpectation('whiteness', false), 'b', peg$literalExpectation('b', false), 'l', peg$literalExpectation('l', false), 's', peg$literalExpectation('s', false), 'w', peg$literalExpectation('w', false), function (op, modifier, value) {
    return {
      op: op,
      modifier: modifier,
      value: value
    };
  }, 'ifDark', peg$literalExpectation('ifDark', false), 'ifLight', peg$literalExpectation('ifLight', false), function (op, color) {
    return {
      op: op,
      color: color
    };
  }, 'blenda', peg$literalExpectation('blenda', false), 'blend', peg$literalExpectation('blend', false), function (op, color, value) {
    return {
      op: op,
      color: color,
      value: value
    };
  }, 'contrast', peg$literalExpectation('contrast', false), function (op, value) {
    return {
      op: op,
      value: value
    };
  }, '.', peg$literalExpectation('.', false), '-', peg$literalExpectation('-', false), /^[0-9]/, peg$classExpectation([['0', '9']], false, false), /^[0-9a-fA-F]/, peg$classExpectation([['0', '9'], ['a', 'f'], ['A', 'F']], false, false), '+', peg$literalExpectation('+', false), '%', peg$literalExpectation('%', false), function () {
    return {
      type: 'percentage',
      value: parseInt(text(), 10)
    };
  }, function () {
    return {
      type: 'number',
      value: parseFloat(text())
    };
  }, /^[a-zA-Z0-9\-_]/, peg$classExpectation([['a', 'z'], ['A', 'Z'], ['0', '9'], '-', '_'], false, false), peg$otherExpectation('whitespace'), /^[ \t\n\r,]/, peg$classExpectation([' ', '\t', '\n', '\r', ','], false, false)];
  var peg$bytecode = [peg$decode('%;".# &;!/\' 8!: !! )'), peg$decode(";&./ &;'.) &;#.# &;$"), peg$decode('%2!""6!7"/Z#;8/Q$;!/H$$;(0#*;(&/8$2#""6#7$/)$8%:%%""!)(%\'#($\'#(#\'#("\'#&\'#'), peg$decode('%%%2&""6&7\'/:#;%/1$;%." &"/#$+#)(#\'#("\'#&\'#/"!&,)/\' 8!:(!! )'), peg$decode('%%%;7/1#;1." &"/#$+")("\'#&\'#/"!&,)/\' 8!:)!! )'), peg$decode("%;2/5#;2/,$;2/#$+#)(#'#(\"'#&'#"), peg$decode("%2*\"\"6*7+.) &2,\"\"6,7-/\x9F#2.\"\"6.7//\x90$;8/\x87$;4/~$;8/u$;4/l$;8/c$;4/Z$;8/Q$;6.\" &\"/C$;8/:$2#\"\"6#7$/+$8,:0,$(&$\")(,'#(+'#(*'#()'#(('#(''#(&'#(%'#($'#(#'#(\"'#&'#"), peg$decode("%21\"\"6172.) &23\"\"6374/\x9F#2.\"\"6.7//\x90$;8/\x87$;6/~$;8/u$;5/l$;8/c$;5/Z$;8/Q$;6.\" &\"/C$;8/:$2#\"\"6#7$/+$8,:5,$(&$\")(,'#(+'#(*'#()'#(('#(''#(&'#(%'#($'#(#'#(\"'#&'#"), peg$decode("%;8/d#;*.G &;+.A &;..; &;/.5 &;0./ &;).) &;,.# &;-/1$;8/($8#:6#!!)(#'#(\"'#&'#"), peg$decode('%27""6778/\x9B#;8/\x92$29""697:.) &2;""6;7</w$;8/n$;".# &;!/_$;8/V$;".# &;!." &"/B$;8/9$2#""6#7$/*$8):=)#&$")()\'#((\'#(\'\'#(&\'#(%\'#($\'#(#\'#("\'#&\'#'), peg$decode('%2>""6>7?.) &2@""6@7A/\x80#2.""6.7//q$;8/h$;3." &"/Z$;8/Q$;5.# &;6/B$;8/9$2#""6#7$/*$8(:B(#\'$")((\'#(\'\'#(&\'#(%\'#($\'#(#\'#("\'#&\'#'), peg$decode('%2C""6C7D.) &2E""6E7F/z#2.""6.7//k$;8/b$;3." &"/T$;8/K$;6/B$;8/9$2#""6#7$/*$8(:B(#\'$")((\'#(\'\'#(&\'#(%\'#($\'#(#\'#("\'#&\'#'), peg$decode('%2G""6G7H.) &2I""6I7J/b#2.""6.7//S$;8/J$;5/A$;8/8$2#""6#7$/)$8&:K&"%")(&\'#(%\'#($\'#(#\'#("\'#&\'#'), peg$decode('%2L""6L7M.q &2N""6N7O.e &2P""6P7Q.Y &2R""6R7S.M &2T""6T7U.A &2V""6V7W.5 &2X""6X7Y.) &2Z""6Z7[/z#2.""6.7//k$;8/b$;3." &"/T$;8/K$;5/B$;8/9$2#""6#7$/*$8(:\\(#\'$")((\'#(\'\'#(&\'#(%\'#($\'#(#\'#("\'#&\'#'), peg$decode('%2]""6]7^.) &2_""6_7`/h#2.""6.7//Y$;8/P$;".# &;!/A$;8/8$2#""6#7$/)$8&:a&"%")(&\'#(%\'#($\'#(#\'#("\'#&\'#'), peg$decode('%2b""6b7c.) &2d""6d7e/{#2.""6.7//l$;8/c$;".# &;!/T$;8/K$;5/B$;8/9$2#""6#7$/*$8(:f(#\'$")((\'#(\'\'#(&\'#(%\'#($\'#(#\'#("\'#&\'#'), peg$decode('%2g""6g7h/g#2.""6.7//X$;8/O$;5." &"/A$;8/8$2#""6#7$/)$8&:i&"%")(&\'#(%\'#($\'#(#\'#("\'#&\'#'), peg$decode('%%2j""6j7k/Y#2l""6l7m." &"/E$$4n""5!7o/,#0)*4n""5!7o&&&#/#$+#)(#\'#("\'#&\'#/"!&,)'), peg$decode('4p""5!7q'), peg$decode('2r""6r7s.) &2l""6l7m'), peg$decode(';5.# &;6'), peg$decode('%;6/6#2t""6t7u/\'$8":v" )("\'#&\'#'), peg$decode('%$4n""5!7o/,#0)*4n""5!7o&&&#/g#%2j""6j7k/E#$4n""5!7o/,#0)*4n""5!7o&&&#/#$+")("\'#&\'#." &"/\'$8":w" )("\'#&\'#'), peg$decode('%$4x""5!7y/,#0)*4x""5!7y&&&#/"!&,)'), peg$decode('<$4{""5!7|0)*4{""5!7|&=." 7z')];
  var peg$currPos = 0;
  var peg$savedPos = 0;
  var peg$posDetailsCache = [{
    line: 1,
    column: 1
  }];
  var peg$maxFailPos = 0;
  var peg$maxFailExpected = [];
  var peg$silentFails = 0;
  var peg$result;

  if (options.startRule !== undefined) {
    if (!(options.startRule in peg$startRuleIndices)) {
      throw new Error("Can't start parsing from rule \"" + options.startRule + "\".");
    }

    peg$startRuleIndex = peg$startRuleIndices[options.startRule];
  }

  function text() {
    return input.substring(peg$savedPos, peg$currPos);
  }

  function peg$literalExpectation(text1, ignoreCase) {
    return {
      type: 'literal',
      text: text1,
      ignoreCase: ignoreCase
    };
  }

  function peg$classExpectation(parts, inverted, ignoreCase) {
    return {
      type: 'class',
      parts: parts,
      inverted: inverted,
      ignoreCase: ignoreCase
    };
  }

  function peg$endExpectation() {
    return {
      type: 'end'
    };
  }

  function peg$otherExpectation(description) {
    return {
      type: 'other',
      description: description
    };
  }

  function peg$computePosDetails(pos) {
    var details = peg$posDetailsCache[pos];
    var p;

    if (details) {
      return details;
    }

    p = pos - 1;

    while (!peg$posDetailsCache[p]) {
      p--;
    }

    details = peg$posDetailsCache[p];
    details = {
      line: details.line,
      column: details.column
    };

    while (p < pos) {
      if (input.charCodeAt(p) === 10) {
        details.line++;
        details.column = 1;
      } else {
        details.column++;
      }

      p++;
    }

    peg$posDetailsCache[pos] = details;
    return details;
  }

  function peg$computeLocation(startPos, endPos) {
    var startPosDetails = peg$computePosDetails(startPos);
    var endPosDetails = peg$computePosDetails(endPos);
    return {
      start: {
        offset: startPos,
        line: startPosDetails.line,
        column: startPosDetails.column
      },
      end: {
        offset: endPos,
        line: endPosDetails.line,
        column: endPosDetails.column
      }
    };
  }

  function peg$fail(expected1) {
    if (peg$currPos < peg$maxFailPos) {
      return;
    }

    if (peg$currPos > peg$maxFailPos) {
      peg$maxFailPos = peg$currPos;
      peg$maxFailExpected = [];
    }

    peg$maxFailExpected.push(expected1);
  }

  function peg$buildStructuredError(expected1, found, location1) {
    return new SyntaxError(SyntaxError.buildMessage(expected1, found), expected1, found, location1);
  }

  function peg$decode(s) {
    return s.split('').map(function (ch) {
      return ch.charCodeAt(0) - 32;
    });
  }

  function peg$parseRule(index) {
    var bc = peg$bytecode[index];
    var ip = 0;
    var ips = [];
    var end = bc.length;
    var ends = [];
    var stack = [];
    var params;

    while (true) {
      while (ip < end) {
        switch (bc[ip]) {
          case 0:
            stack.push(peg$consts[bc[ip + 1]]);
            ip += 2;
            break;

          case 1:
            stack.push(undefined);
            ip++;
            break;

          case 2:
            stack.push(null);
            ip++;
            break;

          case 3:
            stack.push(peg$FAILED);
            ip++;
            break;

          case 4:
            stack.push([]);
            ip++;
            break;

          case 5:
            stack.push(peg$currPos);
            ip++;
            break;

          case 6:
            stack.pop();
            ip++;
            break;

          case 7:
            peg$currPos = stack.pop();
            ip++;
            break;

          case 8:
            stack.length -= bc[ip + 1];
            ip += 2;
            break;

          case 9:
            stack.splice(-2, 1);
            ip++;
            break;

          case 10:
            stack[stack.length - 2].push(stack.pop());
            ip++;
            break;

          case 11:
            stack.push(stack.splice(stack.length - bc[ip + 1], bc[ip + 1]));
            ip += 2;
            break;

          case 12:
            stack.push(input.substring(stack.pop(), peg$currPos));
            ip++;
            break;

          case 13:
            ends.push(end);
            ips.push(ip + 3 + bc[ip + 1] + bc[ip + 2]);

            if (stack[stack.length - 1]) {
              end = ip + 3 + bc[ip + 1];
              ip += 3;
            } else {
              end = ip + 3 + bc[ip + 1] + bc[ip + 2];
              ip += 3 + bc[ip + 1];
            }

            break;

          case 14:
            ends.push(end);
            ips.push(ip + 3 + bc[ip + 1] + bc[ip + 2]);

            if (stack[stack.length - 1] === peg$FAILED) {
              end = ip + 3 + bc[ip + 1];
              ip += 3;
            } else {
              end = ip + 3 + bc[ip + 1] + bc[ip + 2];
              ip += 3 + bc[ip + 1];
            }

            break;

          case 15:
            ends.push(end);
            ips.push(ip + 3 + bc[ip + 1] + bc[ip + 2]);

            if (stack[stack.length - 1] !== peg$FAILED) {
              end = ip + 3 + bc[ip + 1];
              ip += 3;
            } else {
              end = ip + 3 + bc[ip + 1] + bc[ip + 2];
              ip += 3 + bc[ip + 1];
            }

            break;

          case 16:
            if (stack[stack.length - 1] !== peg$FAILED) {
              ends.push(end);
              ips.push(ip);
              end = ip + 2 + bc[ip + 1];
              ip += 2;
            } else {
              ip += 2 + bc[ip + 1];
            }

            break;

          case 17:
            ends.push(end);
            ips.push(ip + 3 + bc[ip + 1] + bc[ip + 2]);

            if (input.length > peg$currPos) {
              end = ip + 3 + bc[ip + 1];
              ip += 3;
            } else {
              end = ip + 3 + bc[ip + 1] + bc[ip + 2];
              ip += 3 + bc[ip + 1];
            }

            break;

          case 18:
            ends.push(end);
            ips.push(ip + 4 + bc[ip + 2] + bc[ip + 3]);

            if (input.substr(peg$currPos, peg$consts[bc[ip + 1]].length) === peg$consts[bc[ip + 1]]) {
              end = ip + 4 + bc[ip + 2];
              ip += 4;
            } else {
              end = ip + 4 + bc[ip + 2] + bc[ip + 3];
              ip += 4 + bc[ip + 2];
            }

            break;

          case 19:
            ends.push(end);
            ips.push(ip + 4 + bc[ip + 2] + bc[ip + 3]);

            if (input.substr(peg$currPos, peg$consts[bc[ip + 1]].length).toLowerCase() === peg$consts[bc[ip + 1]]) {
              end = ip + 4 + bc[ip + 2];
              ip += 4;
            } else {
              end = ip + 4 + bc[ip + 2] + bc[ip + 3];
              ip += 4 + bc[ip + 2];
            }

            break;

          case 20:
            ends.push(end);
            ips.push(ip + 4 + bc[ip + 2] + bc[ip + 3]);

            if (peg$consts[bc[ip + 1]].test(input.charAt(peg$currPos))) {
              end = ip + 4 + bc[ip + 2];
              ip += 4;
            } else {
              end = ip + 4 + bc[ip + 2] + bc[ip + 3];
              ip += 4 + bc[ip + 2];
            }

            break;

          case 21:
            stack.push(input.substr(peg$currPos, bc[ip + 1]));
            peg$currPos += bc[ip + 1];
            ip += 2;
            break;

          case 22:
            stack.push(peg$consts[bc[ip + 1]]);
            peg$currPos += peg$consts[bc[ip + 1]].length;
            ip += 2;
            break;

          case 23:
            stack.push(peg$FAILED);

            if (peg$silentFails === 0) {
              peg$fail(peg$consts[bc[ip + 1]]);
            }

            ip += 2;
            break;

          case 24:
            peg$savedPos = stack[stack.length - 1 - bc[ip + 1]];
            ip += 2;
            break;

          case 25:
            peg$savedPos = peg$currPos;
            ip++;
            break;

          case 26:
            params = bc.slice(ip + 4, ip + 4 + bc[ip + 3]).map(function (p) {
              return stack[stack.length - 1 - p];
            });
            stack.splice(stack.length - bc[ip + 2], bc[ip + 2], peg$consts[bc[ip + 1]].apply(null, params));
            ip += 4 + bc[ip + 3];
            break;

          case 27:
            stack.push(peg$parseRule(bc[ip + 1]));
            ip += 2;
            break;

          case 28:
            peg$silentFails++;
            ip++;
            break;

          case 29:
            peg$silentFails--;
            ip++;
            break;

          default:
            throw new Error("Invalid opcode: " + bc[ip] + ".");
        }
      }

      if (ends.length > 0) {
        end = ends.pop();
        ip = ips.pop();
      } else {
        break;
      }
    }

    return stack[0];
  } // this is taken from https://github.com/ianstormtaylor/css-color-function/blob/master/lib/adjusters.js


  var rgbVal = function rgbVal(value) {
    return value.type === 'percentage' ? value.value * 255 : value.value;
  };

  var contrast = function contrast(ctx, colorLib, source, op) {
    if (!op.value) {
      op.value = {
        type: 'percentage',
        value: 100
      };
    }

    var percentage = op.value.value;
    var max = colorLib(source.luminosity() < 0.5 ? {
      h: source.hue(),
      w: 100,
      b: 0
    } : {
      h: source.hue(),
      w: 0,
      b: 100
    });
    var minRatio = 4.5;
    var min = max;

    if (source.contrast(max) > minRatio) {
      min = binarySearchBWContrast(minRatio, source, max);
      var targetMinAlpha = min.alpha();
      min = min.alpha(1).mix(max, percentage / 100).alpha(targetMinAlpha);
    }

    return min;
  };

  var binarySearchBWContrast = function binarySearchBWContrast(minRatio, color, max) {
    var hue = color.hue();
    var min = color;
    var minW = color.white();
    var minB = color.wblack();
    var maxW = max.white();
    var maxB = max.wblack();

    while (Math.abs(minW - maxW) > 1 || Math.abs(minB - maxB) > 1) {
      var midW = Math.round((maxW + minW) / 2);
      var midB = Math.round((maxB + minB) / 2);
      min = min.white(midW).wblack(midB);

      if (min.contrast(color) > minRatio) {
        maxW = midW;
        maxB = midB;
      } else {
        minW = midW;
        minB = midB;
      }
    }

    return min;
  };

  var modify = function modify(value, op) {
    switch (op.modifier) {
      case '+':
        return value + op.value.value;

      case '-':
        return value - op.value.value;

      default:
        return op.value.value;
    }
  };

  var alpha = function alpha(ctx, colorLib, source, op) {
    if (op.value.type === 'percentage') {
      op.value = {
        value: op.value.value / 100
      };
    }

    return source.alpha(modify(source.alpha(), op));
  };

  var createOp = function createOp(prop) {
    return function (ctx, colorLib, source, op) {
      return source[prop](modify(source[prop](), op));
    };
  };

  var hue = createOp('hue');
  var blackness = createOp('wblack');
  var lightness = createOp('lightness');
  var saturation = createOp('saturationl');
  var whiteness = createOp('white');

  var blend = function blend(ctx, colorLib, source, op) {
    if (op.op === 'blenda') {
      return source.mix(op.color(ctx, colorLib), op.value.value / 100);
    }

    var targetAlpha = source.alpha();
    return source.alpha(1).mix(op.color(ctx, colorLib), op.value.value / 100).alpha(targetAlpha);
  };

  var ifClause = function ifClause(ctx, colorLib, source, op) {
    if (source[op.predicate]()) {
      return op.ifLeaf(ctx, colorLib);
    }

    if (op.elseLeaf) {
      return op.elseLeaf(ctx, colorLib);
    }

    return source;
  };

  var ifContrast = function ifContrast(ctx, colorLib, source, op) {
    return ifClause(ctx, colorLib, source, {
      predicate: op.op === 'ifDark' ? 'isDark' : 'isLight',
      ifLeaf: op.color
    });
  };

  var operations = {
    a: alpha,
    alpha: alpha,
    b: blackness,
    blackness: blackness,
    blend: blend,
    blenda: blend,
    contrast: contrast,
    h: hue,
    hue: hue,
    "if": ifClause,
    ifDark: ifContrast,
    ifLight: ifContrast,
    l: lightness,
    lightness: lightness,
    s: saturation,
    saturation: saturation,
    shade: blend,
    tint: blend,
    w: whiteness,
    whiteness: whiteness
  };

  function createProcessFn(color, ops) {
    return function (ctx, colorLib) {
      var source = colorLib(color(ctx, colorLib));
      return ops.reduce(function (res, op) {
        return operations[op.op](ctx, colorLib, res, op);
      }, source);
    };
  }

  peg$result = peg$parseRule(peg$startRuleIndex);

  if (peg$result !== peg$FAILED && peg$currPos === input.length) {
    return peg$result;
  }

  if (peg$result !== peg$FAILED && peg$currPos < input.length) {
    peg$fail(peg$endExpectation());
  }

  throw peg$buildStructuredError(peg$maxFailExpected, peg$maxFailPos < input.length ? input.charAt(peg$maxFailPos) : null, peg$maxFailPos < input.length ? peg$computeLocation(peg$maxFailPos, peg$maxFailPos + 1) : peg$computeLocation(peg$maxFailPos, peg$maxFailPos));
}

var parse = peg$parse;

var unableToParserColorErrorMessage = 'Unable to parse color from string: ';
/**
 * Default resolver for colors
 */

var color = function resolveColor(propName, value, ctx) {
  var colors = new Set();
  var valueToResolve = value;

  while (true) {
    // if we detect infinite cycle, we just return the value to resolve
    if (colors.has(valueToResolve)) {
      return valueToResolve;
    }

    colors.add(valueToResolve);

    try {
      var resolve = parse(valueToResolve);
      return resolve(ctx, parseColor);
    } catch (e) {
      // first try to resolve the color against the theme
      // if value is the same, then try to parse it again
      var resolvedColorFromTheme = ctx.resolve('colors', valueToResolve, ctx);

      if (valueToResolve === resolvedColorFromTheme) {
        // try to parse again
        continue;
      }

      if (e instanceof Error) {
        if (e.message.startsWith(unableToParserColorErrorMessage)) {
          valueToResolve = e.message.replace(unableToParserColorErrorMessage, '');
        }
      }
    }
  }
};
/**
 * Default resolver for borderRadius and border*Radius
 */


var borderRadius = function resolveBorderRadius(propName, value, ctx) {
  var valueForExactPropName = themeKeyResolver(propName, value, ctx);

  if (valueForExactPropName === value) {
    // there is probably no value, try to use borderRadius value
    return themeKeyResolver('borderRadius', value, ctx);
  }

  return valueForExactPropName;
};
var resolvers = {
  color: color,
  borderRadius: borderRadius,
  themeKey: themeKeyResolver
};

/** Name of CSS properties that should use color resolver */
var colorProps = ['backgroundColor', 'borderColor', 'borderBlockColor', 'borderBottomColor', 'borderLeftColor', 'borderRightColor', 'borderTopColor', 'borderBlockColor', 'borderBlockEndColor', 'borderBlockStartColor', 'borderInlineColor', 'borderInlineEndColor', 'borderInlineStartColor', 'color', 'fill', 'outlineColor', 'stroke'];
/** Name of CSS properties that should use borderRadius theme value or their own theme value */

var borderRadiusProps = ['borderRadius', 'bordeTopLeftRadius', 'bordeTopRightRadius', 'bordeBottomLeftRadius', 'bordeBottomRightRadius'];

var stylers = /*#__PURE__*/_extends({}, /*#__PURE__*/colorProps.reduce(function (colorStylers, propName) {
  var _extends2;

  return _extends({}, colorStylers, (_extends2 = {}, _extends2[propName] = {
    resolver: 'color'
  }, _extends2));
}, {}), /*#__PURE__*/borderRadiusProps.reduce(function (borderRadiusStylers, propName) {
  var _extends3;

  return _extends({}, borderRadiusStylers, (_extends3 = {}, _extends3[propName] = {
    resolver: 'borderRadius'
  }, _extends3));
}, {}), {
  catchAll: catchAllStyler,
  "extends": extendsStyler,
  face: faceStyler
});

function createStylerFromSettings(name, settings) {
  var type = settings.type || StylerResultType.inPlaceFinal;
  var outputProps = [name];

  if (typeof settings.outputProps === 'string') {
    outputProps = [settings.outputProps];
  } else if (Array.isArray(settings.outputProps) && settings.outputProps.length > 0) {
    outputProps = settings.outputProps;
  }

  var outputPropsLength = outputProps.length;

  var styler = function styler(propName, value, ctx) {
    var _ref;

    var resolvedValue = value;
    var resolver = (_ref = settings.resolver && ctx.resolvers[settings.resolver]) != null ? _ref : ctx.resolvers.themeKey;
    resolvedValue = resolver(propName, value, ctx); // now if the value is an object we can't use formatter

    if (typeof resolvedValue === 'object' && resolvedValue !== null) {
      return resolvedValue;
    }

    if (settings.format && ctx.formatters[settings.format]) {
      resolvedValue = ctx.formatters[settings.format](propName, resolvedValue, ctx);
    }

    var styles = {};

    for (var i = 0; i < outputPropsLength; i++) {
      styles[outputProps[i]] = resolvedValue;
    }

    return styles;
  };

  return createStyler(type, styler);
}

function check(type, name, map) {
  if (map[name] == null) {
    throw new Error(type + " " + name + " is missing");
  }
}

function createTheme(_ref) {
  var _ref$formatters = _ref.formatters,
      formatters$1 = _ref$formatters === void 0 ? {} : _ref$formatters,
      _ref$resolvers = _ref.resolvers,
      resolvers$1 = _ref$resolvers === void 0 ? {} : _ref$resolvers,
      _ref$stylers = _ref.stylers,
      stylers$1 = _ref$stylers === void 0 ? {} : _ref$stylers,
      theme = _ref.theme;

  var allFormatters = _extends({}, formatters, formatters$1);

  var allResolvers = _extends({}, resolvers, resolvers$1);

  var allStylers = _extends({}, stylers, stylers$1); // const faces: TFaces = themeSettings.faces || ({} as TFaces);


  var resolvedStylers = {};
  Object.keys(allStylers).forEach(function (propName) {
    var settings = allStylers[propName];

    if (typeof settings === 'function') {
      resolvedStylers[propName] = settings;
    } else {
      if (settings.resolver) {
        check('resolver', settings.resolver, allResolvers);
      }

      if (settings.format) {
        check('formatter', settings.format, allFormatters);
      }

      resolvedStylers[propName] = createStylerFromSettings(propName, settings);
    }
  });
  return {
    formatters: allFormatters,
    resolvers: allResolvers,
    stylers: resolvedStylers,
    format: function format(formatterName, propValue, ctx) {
      if (allFormatters[formatterName]) {
        return allFormatters[formatterName](formatterName, propValue, ctx);
      }

      return propValue;
    },
    resolve: function resolve(resolverName, propValue, ctx) {
      var _allResolvers$resolve;

      return ((_allResolvers$resolve = allResolvers[resolverName]) != null ? _allResolvers$resolve : allResolvers.themeKey)(resolverName, propValue, ctx);
    },
    style: function style(resolverName, propValue, ctx) {
      var _resolvedStylers$reso;

      var cacheKey = resolverName + "::" + propValue + "::" + ctx.breakpoint;

      if (ctx.stylerCache.has(cacheKey)) {
        return ctx.stylerCache.get(cacheKey);
      }

      var res = ((_resolvedStylers$reso = resolvedStylers[resolverName]) != null ? _resolvedStylers$reso : resolvedStylers.catchAll)(resolverName, propValue, ctx);
      ctx.stylerCache.set(cacheKey, res);
      return res;
    },
    theme: theme
  };
}

export { DesignSystem, IsBreakpoint, StylerResultType, borderRadius, catchAllStyler, createCache, createComponent, createStyler, createStylerFromSettings, createTheme, displayName, extendsStyler, faceStyler, formatters, inPlaceFinalStyler, inPlaceStyler, isVisageComponent, markAsVisageComponent, postFinalStyler, postStyler, preFinalStyler, preStyler, resolveStyleSheet, resolveStyleSheets, resolvers, stylers, themeKeyResolver, useBreakpoint, useBreakpointManager, useDesignSystem, useStaticMemo, useVariantSettings, useVisage };
//# sourceMappingURL=byteclaw.visage.core.esm.js.map
